import { Link } from 'react-router';
import { cn } from '@helios/ui';
import type { ResolvedHubPhase } from './subjectHub';

/**
 * Dense horizontal process map for a subject hub. This is orientation, not
 * completion tracking — every visible phase is a destination, and only the
 * phase that owns the open tab is current.
 */
export function ProcessPhaseRail({
  phases,
  activePhaseId,
  ariaLabel,
}: {
  phases: readonly ResolvedHubPhase[];
  activePhaseId?: string;
  ariaLabel: string;
}) {
  if (phases.length === 0) return null;

  return (
    <nav aria-label={ariaLabel} className="min-w-0 flex-1 overflow-x-auto pb-1.5">
      <ol className="flex min-w-min items-center">
        {phases.map((phase, index) => {
          const current = phase.id === activePhaseId;
          const Icon = phase.icon;
          return (
            <li key={phase.id} className="flex items-center">
              {index > 0 ? (
                <span
                  aria-hidden="true"
                  className="bg-border/60 mx-0.5 h-px w-2 shrink-0 sm:mx-1 sm:w-3"
                />
              ) : null}
              <Link
                to={phase.to}
                aria-current={current ? 'step' : undefined}
                title={phase.tabLabels.join(' · ')}
                className={cn(
                  'text-2xs inline-flex items-center gap-1 rounded-md border border-transparent px-1.5 py-1 font-semibold tracking-wide transition-colors',
                  'focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-2',
                  current
                    ? 'border-context-primary/35 bg-context-primary/10 text-context-primary'
                    : 'text-muted-foreground hover:border-border/50 hover:bg-secondary/50 hover:text-foreground',
                )}
              >
                <span
                  className={cn(
                    'inline-flex h-4 min-w-4 items-center justify-center rounded-full px-0.5 tabular-nums',
                    current
                      ? 'bg-context-primary/20 text-foreground'
                      : 'bg-secondary/80 text-muted-foreground',
                  )}
                >
                  {phase.step}
                </span>
                <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
                <span className="whitespace-nowrap">{phase.label}</span>
              </Link>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
